import React, { useState, useEffect } from 'react';
import { useRecoilState } from 'recoil';
import { useMount } from 'react-use';

import { NotificationIcon, NotificationUnreadIcon } from '@stockx/icons';
import { IconButton, Button, Tooltip, Text, Box } from '@stockx/ui';

import { useGATrackEvent, Events } from '@app/analytics';
import { useHistory, useUrlBuilder } from '@app/router';
import { trackEvent as segmentTrackEvent, EVENTS } from '@app/segment';
import { useUserIsLoggedIn } from '@app/user';
import { EXPERIMENTS } from '@app/config';
import { useExperiment } from '@app/experiments';
import useExponentialChronology from '@app/hooks/useExponentialChronology';

import {
  useNewInboxMessages,
  useShowDot,
  useOffersMessageList,
} from '../../hooks';
import { inboxDropdownOpenedState } from '../../state';
import { InboxReminderTooltipCookie } from '../../consts';

const MobileInboxIcon = () => {
  // green dot should only be clickable once per session
  // recoil state is persisting to session storage here
  // For control of crm promo test
  const [iconClicked, setIconClicked] = useRecoilState(
    inboxDropdownOpenedState,
  );

  const [tooltipOpen, setTooltipOpen] = useState(false);

  const abCrmPromoFlag = useExperiment(
    EXPERIMENTS.ab_pirate_crm_promo_web,
  ) as string;

  const isAbCrmPromoVariant = abCrmPromoFlag === 'true';
  const isAbCrmPromoControl = abCrmPromoFlag === 'false';

  const trackGAEvent = useGATrackEvent();
  const loggedIn = useUserIsLoggedIn();
  const buildUrl = useUrlBuilder();
  const history = useHistory();

  // For control of crm promo test
  const newMessages = useNewInboxMessages();

  const { showDot, clearDot } = useShowDot({
    enabled: isAbCrmPromoVariant || isAbCrmPromoControl,
  });

  const { totalRecords } = useOffersMessageList({
    inDropdown: false,
    enabled: isAbCrmPromoVariant || isAbCrmPromoControl,
  });

  const promotionsCount = totalRecords || 0;

  const displayDot = isAbCrmPromoVariant
    ? !loggedIn || showDot
    : newMessages && !iconClicked;

  const messageCenterUrl = buildUrl('/message-center');

  const { increment } = useExponentialChronology(() => setTooltipOpen(true), {
    key: InboxReminderTooltipCookie,
    enabled: isAbCrmPromoVariant || isAbCrmPromoControl,
    firstVisitOnly: false,
    resetPeriod: 'V1',
    bufferNum: 1,
  });

  const onCloseTooltip = () => {
    if (
      isAbCrmPromoVariant &&
      tooltipOpen &&
      (!loggedIn || promotionsCount > 0)
    ) {
      trackGAEvent(Events.InboxTooltipDismissed);
      segmentTrackEvent(EVENTS.INBOX_TOOLTIP_DISMISSED, {});

      setTooltipOpen(false);
    }
  };

  useMount(() => {
    increment();
  });

  useEffect(() => {
    // Need for variant and control for counterfactual
    if (tooltipOpen && (isAbCrmPromoVariant || isAbCrmPromoControl)) {
      trackGAEvent(Events.InboxTooltipImpression);
      segmentTrackEvent(EVENTS.INBOX_TOOLTIP_IMPRESSION, {});
    }
  }, [
    isAbCrmPromoControl,
    isAbCrmPromoVariant,
    loggedIn,
    promotionsCount,
    tooltipOpen,
    trackGAEvent,
  ]);

  return (
    <Tooltip
      p={3}
      maxWidth="300px"
      background="neutral.700"
      shouldWrapChildren
      hasArrow
      label={
        <Box>
          <Text
            fontSize="xs"
            fontWeight={400}
            lineHeight="18px"
            _hover={{ color: 'neutral.white' }}
          >
            {!loggedIn
              ? `Login or sign up to get alerts about offers and products you're
                following.`
              : `You have ${promotionsCount} ${
                  promotionsCount > 1 ? 'offers' : 'offer'
                } available - check them out in the message center! `}
          </Text>
          <Button
            variant="link-underline-dark"
            fontSize="xs"
            p={0}
            onClick={onCloseTooltip}
          >
            {'Got it'}
          </Button>
        </Box>
      }
      placement="bottom-end"
      pointerEvents="auto"
      isOpen={
        isAbCrmPromoVariant && tooltipOpen && (!loggedIn || promotionsCount > 0)
      }
      closeOnClick
      closeOnPointerDown
      closeOnScroll
      onClose={onCloseTooltip}
    >
      <IconButton
        size="lg"
        bg="transparent"
        h={6}
        w={6}
        icon={displayDot ? <NotificationUnreadIcon /> : <NotificationIcon />}
        aria-label={
          displayDot ? 'Notification Unread Icon' : 'Notification Icon'
        }
        data-testid={
          displayDot ? 'notification-unread-icon' : 'notification-icon'
        }
        _hover={{ cursor: 'pointer' }}
        _active={{ bg: 'transparent' }}
        onClick={() => {
          if (displayDot) {
            if (isAbCrmPromoVariant) {
              if (loggedIn) {
                clearDot();
              }
            } else {
              setIconClicked(true);
            }
          }

          trackGAEvent(Events.InboxIconClicked);
          segmentTrackEvent(EVENTS.INBOX_ICON_CLICKED, {});

          history.push(messageCenterUrl);
        }}
      />
    </Tooltip>
  );
};

export default MobileInboxIcon;
